// src/pages/admin/UsersSection.tsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  Eye,
  EyeOff,
  Search,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Pencil,
  Trash2,
  RefreshCw,
  Upload,
  FileSpreadsheet,
  FileDown,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

/* ===================== API helpers ===================== */

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";

function cleanBearer(t?: string | null) {
  let raw = (t || "").trim();
  if (raw.toLowerCase().startsWith("bearer ")) raw = raw.slice(7).trim();
  return raw;
}

type RoleAPI = "Admin" | "Manager" | "User";

export type UserDTO = {
  id: number;
  name: string;
  role: RoleAPI;
  function?: string | null;
  email: string;
  must_change_password?: boolean;
};

// ---- util pour sortir des erreurs 422 lisibles (FastAPI/Pydantic) ----
function parseFastApiErrorPayload(data: any): string {
  if (!data) return "Requête invalide";
  if (typeof data.detail === "string") return data.detail;
  if (Array.isArray(data.detail)) {
    // format Pydantic: [{loc: ['body','field'], msg:'...', type:'...'}, ...]
    const msgs = data.detail.map((e: any) => {
      const loc = Array.isArray(e?.loc) ? e.loc.join(".") : "";
      const msg = e?.msg || "invalide";
      return loc ? `${loc}: ${msg}` : msg;
    });
    return msgs.join(" • ");
  }
  return data?.detail || "Requête invalide";
}

async function apiListUsers(): Promise<UserDTO[]> {
  const stored = localStorage.getItem("auth_token");
  const token = cleanBearer(stored);
  const res = await fetch(`${API_BASE}/users`, {
    headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    let detail = "Échec du chargement des utilisateurs";
    try {
      const data = await res.json();
      detail = data?.detail || detail;
    } catch {}
    throw new Error(detail);
  }
  return res.json();
}

async function apiCreateUser(payload: {
  email: string;
  name: string;
  role: RoleAPI;
  password: string;
  function?: string | null;
}) {
  // garde-fous côté front pour éviter 422 inutiles
  const email = (payload.email || "").trim().toLowerCase();
  const name = (payload.name || "").trim();
  const password = (payload.password || "").trim();
  const role = payload.role;
  const fn = payload.function ?? null;

  if (!email) throw new Error("L’email est requis.");
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) throw new Error("Format d’email invalide.");
  if (!name) throw new Error("Le nom est requis.");
  if (password.length < 6) throw new Error("Le mot de passe doit contenir au moins 6 caractères.");
  if (!["Admin", "Manager", "User"].includes(role)) throw new Error("Rôle invalide.");

  const body = { email, name, role, password, function: fn };

  const stored = localStorage.getItem("auth_token");
  const token = cleanBearer(stored);
  const res = await fetch(`${API_BASE}/users`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg =
      res.status === 422
        ? parseFastApiErrorPayload(data)
        : data?.detail || "Création impossible";
    throw new Error(msg);
  }
  return data as UserDTO;
}

async function apiUpdateUser(
  id: number,
  payload: { name: string; email: string; role: RoleAPI; function?: string | null }
) {
  const stored = localStorage.getItem("auth_token");
  const token = cleanBearer(stored);
  // normaliser côté front
  const body = {
    email: (payload.email || "").trim().toLowerCase(),
    name: (payload.name || "").trim(),
    role: payload.role,
    function: payload.function ?? null,
  };
  const res = await fetch(`${API_BASE}/users/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg =
      res.status === 422
        ? parseFastApiErrorPayload(data)
        : data?.detail || (res.status === 409 ? "Cet email est déjà utilisé." : "Mise à jour impossible");
    throw new Error(msg);
  }
  return data as UserDTO;
}

async function apiDeleteUser(id: number) {
  const stored = localStorage.getItem("auth_token");
  const token = cleanBearer(stored);
  const res = await fetch(`${API_BASE}/users/${id}`, {
    method: "DELETE",
    headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    let msg = "Échec de la suppression";
    try {
      const d = await res.json();
      msg = d?.detail || msg;
    } catch {}
    throw new Error(msg);
  }
}

async function apiImportUsersExcel(file: File) {
  const stored = localStorage.getItem("auth_token");
  const token = cleanBearer(stored);
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${API_BASE}/users/import-excel`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: fd,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || `Import impossible (${res.status})`);
  return data as { created: number; skipped: number; errors: Array<{ row: string; error: string }> };
}

async function apiExportUsersExcel() {
  const stored = localStorage.getItem("auth_token");
  const token = cleanBearer(stored);
  const res = await fetch(`${API_BASE}/users/export-excel`, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    let msg = "Export impossible";
    try {
      const d = await res.json();
      msg = d?.detail || msg;
    } catch {}
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "utilisateurs.xlsx";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

async function apiDownloadImportTemplate() {
  const stored = localStorage.getItem("auth_token");
  const token = cleanBearer(stored);
  const res = await fetch(`${API_BASE}/users/import-template`, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Téléchargement du modèle impossible");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "modele_import_utilisateurs.xlsx";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ✅ nouveau : définir le mot de passe (admin)
async function apiSetUserPassword(id: number, newPassword: string, forceChange: boolean) {
  if ((newPassword || "").length < 6) {
    throw new Error("Le mot de passe doit contenir au moins 6 caractères.");
  }
  const stored = localStorage.getItem("auth_token");
  const token = cleanBearer(stored);
  const res = await fetch(`${API_BASE}/users/${id}/set-password`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ new_password: newPassword, force_change: !!forceChange }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg =
      res.status === 422
        ? parseFastApiErrorPayload(data)
        : data?.detail || `Changement de mot de passe impossible (${res.status})`;
    throw new Error(msg);
  }
  return data as UserDTO;
}

/* ========================= Types UI ========================= */

export type Group = "ADMIN" | "MANAGER" | "USER";

export type UserRow = {
  id: number;
  name: string;
  email: string;
  group: Group;
  function?: string | null;
  active: boolean;
  must_change_password?: boolean;
};

/* ========================= Public API ========================= */

export function UsersSection({
  subTab,
  setSubTab,
}: {
  subTab: "gestion" | "ajouter";
  setSubTab: (v: "gestion" | "ajouter") => void;
}) {
  const [refreshFlag, setRefreshFlag] = useState(0);
  const triggerRefresh = () => setRefreshFlag((n) => n + 1);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-center gap-2">
        <SubTabButton
          active={subTab === "gestion"}
          onClick={() => setSubTab("gestion")}
          label="Gestion des utilisateurs"
        />
        <SubTabButton
          active={subTab === "ajouter"}
          onClick={() => setSubTab("ajouter")}
          label="Ajouter un nouvel utilisateur"
        />
      </div>

      {subTab === "gestion" ? (
        <UsersGestion refreshFlag={refreshFlag} onManualRefresh={triggerRefresh} />
      ) : (
        <UserCreate
          onCreated={() => {
            setSubTab("gestion");
            triggerRefresh();
          }}
        />
      )}
    </div>
  );
}

/* ========================= Utilitaires ========================= */

type SortKey = "name" | "email" | "group" | "function";
type SortDir = "asc" | "desc";

function mapRoleToGroup(role: RoleAPI): Group {
  return role === "Admin" ? "ADMIN" : role === "Manager" ? "MANAGER" : "USER";
}
function mapGroupToRole(g: Group): RoleAPI {
  return g === "ADMIN" ? "Admin" : g === "MANAGER" ? "Manager" : "User";
}

/* ========================= Gestion ========================= */

function UsersGestion({
  refreshFlag,
  onManualRefresh,
}: {
  refreshFlag: number;
  onManualRefresh: () => void;
}) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<UserRow[]>([]);
  const [q, setQ] = useState("");
  const [group, setGroup] = useState<"ALL" | Group>("ALL");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const [editOpen, setEditOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selected, setSelected] = useState<UserRow | undefined>(undefined);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiListUsers();
      const mapped: UserRow[] = data.map((u) => ({
        id: u.id,
        name: u.name,
        email: u.email,
        group: mapRoleToGroup(u.role),
        function: u.function ?? null,
        active: true,
        must_change_password: !!u.must_change_password,
      }));
      setRows(mapped);
    } catch (e: any) {
      toast({
        title: "Chargement impossible",
        description: e?.message || "Erreur inconnue",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load, refreshFlag]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter((u) => {
      const matchesQ =
        !term ||
        u.name.toLowerCase().includes(term) ||
        u.email.toLowerCase().includes(term);
      const matchesGroup = group === "ALL" ? true : u.group === group;
      return matchesQ && matchesGroup;
    });
  }, [rows, q, group]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let aVal: any = a[sortKey];
      let bVal: any = b[sortKey];
      if (aVal == null) aVal = "";
      if (bVal == null) bVal = "";
      const res = String(aVal).localeCompare(String(bVal));
      return sortDir === "asc" ? res : -res;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);

  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, currentPage, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [rows, pageSize]);

  const toggleSort = (key: SortKey) => {
    setPage(1);
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const handleImportClick = () => inputRef.current?.click();

  const handleImportSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.currentTarget.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      toast({ title: "Format invalide", description: "Sélectionnez un .xlsx", variant: "destructive" });
      return;
    }
    try {
      const res = await apiImportUsersExcel(file);
      const errs = res.errors?.length || 0;
      toast({
        title: "Import terminé ✅",
        description: `Créés: ${res.created} • Ignorés (doublons/lignes vides): ${res.skipped} • Erreurs: ${errs}`,
      });
      if (errs > 0) {
        console.warn("Erreurs d'import:", res.errors);
      }
      onManualRefresh();
    } catch (err: any) {
      toast({ title: "Échec import", description: err?.message || "Erreur inconnue", variant: "destructive" });
    }
  };

  const doExport = async () => {
    try {
      await apiExportUsersExcel(); // SANS mots de passe
    } catch (err: any) {
      alert(err?.message || "Export impossible");
    }
  };

  const doDownloadTemplate = async () => {
    try {
      await apiDownloadImportTemplate();
    } catch (err: any) {
      alert(err?.message || "Téléchargement impossible");
    }
  };

  const handleSaveDetails = async (updated: UserRow) => {
    try {
      const api = await apiUpdateUser(updated.id, {
        email: updated.email.trim().toLowerCase(),
        name: updated.name.trim(),
        role: mapGroupToRole(updated.group),
        function: updated.function ?? null,
      });
      setRows((prev) =>
        prev.map((r) =>
          r.id === updated.id
            ? {
                ...r,
                name: api.name,
                email: api.email,
                group: mapRoleToGroup(api.role),
                function: api.function ?? null,
                must_change_password: !!api.must_change_password,
              }
            : r
        )
      );
    } catch (e: any) {
      throw e;
    }
  };

  const handleSetPassword = async (args: { id: number; newPassword: string; forceChange: boolean }) => {
    try {
      const api = await apiSetUserPassword(args.id, args.newPassword, args.forceChange);
      setRows((prev) =>
        prev.map((r) =>
          r.id === args.id
            ? { ...r, must_change_password: !!api.must_change_password }
            : r
        )
      );
    } catch (e: any) {
      throw e;
    }
  };

  const handleConfirmDelete = async () => {
    if (!selected) return;
    try {
      await apiDeleteUser(selected.id);
      setRows((prev) => prev.filter((r) => r.id !== selected.id));
      setConfirmOpen(false);
      setSelected(undefined);
    } catch (e: any) {
      setConfirmOpen(false);
      setSelected(undefined);
      alert(e?.message || "Suppression impossible");
    }
  };

  return (
    <Card className="p-8 bg-gradient-card backdrop-blur-sm border-glass">
      <div className="flex items-center justify-between gap-3 mb-6">
        <h2 className="text-2xl font-semibold">Gestion des Utilisateurs</h2>
        <div className="flex items-center gap-2">
          <input ref={inputRef} type="file" accept=".xlsx" className="hidden" onChange={handleImportSelected} />
          <Button variant="outline" size="sm" className="gap-2" onClick={handleImportClick} title="Importer .xlsx">
            <Upload className="h-4 w-4" />
            Import Excel
          </Button>
          <Button variant="outline" size="sm" className="gap-2" onClick={doExport} title="Exporter (sans mots de passe)">
            <FileSpreadsheet className="h-4 w-4" />
            Export Excel
          </Button>
          <Button variant="outline" size="sm" className="gap-2" onClick={doDownloadTemplate} title="Modèle d’import (.xlsx)">
            <FileDown className="h-4 w-4" />
            Modèle d’import
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => {
              setSelected(undefined);
              setEditOpen(false);
              onManualRefresh();
            }}
            title="Rafraîchir"
          >
            <RefreshCw className="h-4 w-4" />
            Reload
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between mb-6">
        <div className="flex items-center gap-2 max-w-md w-full">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher par nom ou email…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <div className="flex items-center gap-3">
          <Select value={group} onValueChange={(v) => { setGroup(v as any); setPage(1); }}>
            <SelectTrigger className="w-[170px]">
              <SelectValue placeholder="Groupe" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Tous les groupes</SelectItem>
              <SelectItem value="ADMIN">ADMIN</SelectItem>
              <SelectItem value="MANAGER">MANAGER</SelectItem>
              <SelectItem value="USER">USER</SelectItem>
            </SelectContent>
          </Select>

          {totalPages > 1 && (
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Taille page" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5 / page</SelectItem>
                <SelectItem value="10">10 / page</SelectItem>
                <SelectItem value="20">20 / page</SelectItem>
                <SelectItem value="50">50 / page</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Headers */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pb-3 text-sm text-muted-foreground border-b">
        <button className="text-left font-medium flex items-center gap-1" onClick={() => toggleSort("name")}>
          Nom complet
        </button>
        <button className="text-left font-medium flex items-center gap-1" onClick={() => toggleSort("email")}>
          Email
        </button>
        <div className="flex items-center justify-between sm:justify-end gap-3">
          <button className="text-left font-medium flex items-center gap-1" onClick={() => toggleSort("group")}>
            Groupe
          </button>
          <button className="text-left font-medium hidden sm:flex items-center gap-1" onClick={() => toggleSort("function")}>
            Fonction
          </button>
          <span className="w-20" />
        </div>
      </div>

      {/* List */}
      <div className="space-y-6 mt-4">
        {loading ? (
          <Card className="p-6 border-dashed"><p className="text-sm text-muted-foreground">Chargement…</p></Card>
        ) : pageItems.length > 0 ? (
          pageItems.map((u) => (
            <div key={u.id} className="border-b last:border-b-0 pb-6">
              <div className="grid sm:grid-cols-3 gap-2">
                <div>
                  <h3 className="font-semibold">{u.name}</h3>
                  <p className="text-sm">
                    Email: <a href={`mailto:${u.email}`} className="text-primary underline">{u.email}</a>
                  </p>
                </div>

                <div className="text-sm text-muted-foreground">
                  Groupe: <span className="font-medium">{u.group}</span>
                  {u.function ? <span className="ml-2">• Fonction: <span className="font-medium">{u.function}</span></span> : null}
                  {u.must_change_password ? <span className="ml-2 text-amber-600">• changement requis</span> : null}
                </div>

                <div className="flex items-center justify-between sm:justify-end gap-2">
                  <Badge variant={u.active ? "secondary" : "outline"} className="gap-1">
                    <CheckCircle2 className={cn("h-3.5 w-3.5", u.active ? "text-green-600" : "text-muted-foreground")} />
                    {u.active ? "Actif" : "Inactif"}
                  </Badge>

                  <Button variant="outline" size="sm" className="gap-2" onClick={() => { setSelected(u); setEditOpen(true); }}>
                    <Pencil className="h-4 w-4" />
                    Modifier
                  </Button>

                  <Button variant="destructive" size="sm" className="gap-2" onClick={() => { setSelected(u); setConfirmOpen(true); }}>
                    <Trash2 className="h-4 w-4" />
                    Supprimer
                  </Button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <Card className="p-6 border-dashed"><p className="text-sm text-muted-foreground">Aucun utilisateur trouvé.</p></Card>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {total} utilisateur{total > 1 ? "s" : ""} • page {currentPage} / {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="gap-1">
              <ChevronLeft className="h-4 w-4" /> Précédent
            </Button>
            <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="gap-1">
              Suivant <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Modals */}
      <EditUserDialog
        open={editOpen}
        user={selected}
        onOpenChange={(o) => { setEditOpen(o); if (!o) setSelected(undefined); }}
        onSaveDetails={async (u) => {
          try {
            await handleSaveDetails(u);
          } catch (e: any) {
            alert(e?.message || "Échec de la mise à jour");
          }
        }}
        onSetPassword={async (p) => {
          try {
            await handleSetPassword(p);
          } catch (e: any) {
            alert(e?.message || "Échec du changement de mot de passe");
          }
        }}
      />

      <Dialog open={confirmOpen} onOpenChange={(o) => { setConfirmOpen(o); if (!o) setSelected(undefined); }}>
        <DialogContent className="sm:max-w-[480px]" aria-describedby="delete-desc">
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription id="delete-desc">Cette action est définitive.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Supprimer <span className="font-medium">{selected?.name}</span> ({selected?.email}) ?
          </p>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Annuler</Button>
            <Button variant="destructive" onClick={handleConfirmDelete}>Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ========================= Edit Dialog (compact) ========================= */

function EditUserDialog({
  open,
  user,
  onOpenChange,
  onSaveDetails,
  onSetPassword,
}: {
  open: boolean;
  user?: UserRow;
  onOpenChange: (o: boolean) => void;
  onSaveDetails: (u: UserRow) => Promise<void>;
  onSetPassword: (args: { id: number; newPassword: string; forceChange: boolean }) => Promise<void>;
}) {
  const { toast } = useToast();
  const [state, setState] = useState<UserRow | undefined>(user);

  // champs mot de passe
  const [pwd1, setPwd1] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);
  const [forceChange, setForceChange] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setState(user);
    setPwd1("");
    setPwd2("");
    setForceChange(false);
  }, [user]);

  if (!state) return null;

  const saveAll = async () => {
    setBusy(true);
    try {
      // changer le mot de passe si renseigné
      if (pwd1 || pwd2) {
        if (pwd1.length < 6) throw new Error("Le mot de passe doit contenir au moins 6 caractères.");
        if (pwd1 !== pwd2) throw new Error("Les deux mots de passe ne correspondent pas.");
        await onSetPassword({ id: state.id, newPassword: pwd1, forceChange });
      }
      // validations simples
      if (!state.name.trim()) throw new Error("Le nom est requis.");
      if (!state.email.trim()) throw new Error("L’email est requis.");
      await onSaveDetails(state);
      onOpenChange(false);
    } catch (e: any) {
      toast({
        title: "Échec de l’enregistrement",
        description: e?.message || "Erreur inconnue",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* modal plus petit */}
      <DialogContent className="sm:max-w-[480px] p-4" aria-describedby="edit-desc">
        <DialogHeader className="pb-2">
          <DialogTitle>Modifier l’utilisateur</DialogTitle>
          <DialogDescription id="edit-desc">
            Mettre à jour les informations et/ou changer le mot de passe.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          {/* infos principales en 2 colonnes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="u_name">Nom complet</Label>
              <Input id="u_name" value={state.name} onChange={(e) => setState({ ...state, name: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="u_email">Email</Label>
              <Input id="u_email" type="email" value={state.email} onChange={(e) => setState({ ...state, email: e.target.value })} />
            </div>
          </div>

          <div className="space-y-1">
            <Label>Groupe</Label>
            <Select value={state.group} onValueChange={(v) => setState({ ...state, group: v as Group })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ADMIN">ADMIN</SelectItem>
                <SelectItem value="MANAGER">MANAGER</SelectItem>
                <SelectItem value="USER">USER</SelectItem>
              </SelectContent>
            </Select>
          </div>

            {/* ---- Zone changement mot de passe (compact) ---- */}
          <div className="mt-2 rounded-md border p-3 bg-muted/30">
            <div className="font-medium mb-2">Changer le mot de passe</div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="pwd1">Nouveau</Label>
                <div className="relative">
                  <Input
                    id="pwd1"
                    type={show1 ? "text" : "password"}
                    value={pwd1}
                    onChange={(e) => setPwd1(e.target.value)}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShow1((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    aria-label={show1 ? "Masquer" : "Afficher"}
                  >
                    {show1 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="pwd2">Confirmer</Label>
                <div className="relative">
                  <Input
                    id="pwd2"
                    type={show2 ? "text" : "password"}
                    value={pwd2}
                    onChange={(e) => setPwd2(e.target.value)}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShow2((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    aria-label={show2 ? "Masquer" : "Afficher"}
                  >
                    {show2 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mt-2">
              <Label htmlFor="forceChange">Forcer le changement à la prochaine connexion</Label>
              <input
                id="forceChange"
                type="checkbox"
                className="h-4 w-4 accent-current"
                checked={forceChange}
                onChange={(e) => setForceChange(e.target.checked)}
              />
            </div>

            <p className="mt-1 text-xs text-muted-foreground">
              Laissez les champs vides si vous ne souhaitez pas modifier le mot de passe.
            </p>
          </div>
        </div>

        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annuler</Button>
          <Button onClick={saveAll} disabled={busy}>
            {busy ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ========================= Création ========================= */

function UserCreate({ onCreated }: { onCreated: () => void }) {
  const { toast } = useToast();
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", group: "ADMIN" as Group, func: "" });

  const onChange =
    (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      // validations client pour éviter 422
      const name = form.name.trim();
      const email = form.email.trim().toLowerCase();
      const password = form.password.trim();
      if (!name) throw new Error("Le nom est requis.");
      if (!email) throw new Error("L’email est requis.");
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) throw new Error("Format d’email invalide.");
      if (password.length < 6) throw new Error("Le mot de passe doit contenir au moins 6 caractères.");

      const role: RoleAPI = form.group === "ADMIN" ? "Admin" : form.group === "MANAGER" ? "Manager" : "User";
      await apiCreateUser({
        email,
        name,
        role,
        password,
        function: form.func.trim() ? form.func.trim() : null, // <- null, pas undefined
      });
      toast({ title: "Utilisateur créé ✅", description: `${email} (${role})` });
      setForm({ name: "", email: "", password: "", group: "ADMIN", func: "" });
      onCreated();
    } catch (err: any) {
      toast({ title: "Échec de la création", description: err?.message || "Erreur inconnue", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-8 bg-gradient-card backdrop-blur-sm border-glass">
      <h2 className="text-2xl font-semibold mb-6">Ajouter un nouvel utilisateur</h2>

      <form onSubmit={onSubmit} className="grid gap-6">
        <div className="space-y-2">
          <Label htmlFor="name">Nom complet</Label>
          <Input id="name" placeholder="Ex: SKALANTE Hamza" value={form.name} onChange={onChange("name")} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email (identifiant)</Label>
          <Input id="email" type="email" placeholder="ex: user@company.com" value={form.email} onChange={onChange("email")} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Mot de passe</Label>
          <div className="relative">
            <Input id="password" type={showPwd ? "text" : "password"} placeholder="••••••••" value={form.password} onChange={onChange("password")} className="pr-10" />
            <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}>
              {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <p className="text-xs text-muted-foreground">Au moins 6 caractères.</p>
        </div>

        <div className="space-y-2">
          <Label>Groupe (rôle)</Label>
          <Select value={form.group} onValueChange={(v) => setForm((s) => ({ ...s, group: v as Group }))}>
            <SelectTrigger className="bg-background/50 border-glass"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ADMIN">ADMIN</SelectItem>
              <SelectItem value="MANAGER">MANAGER</SelectItem>
              <SelectItem value="USER">USER</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="func">Fonction (optionnel)</Label>
          <Input id="func" placeholder="Ex: Finance, Marketing…" value={form.func} onChange={onChange("func")} />
        </div>

        <div>
          <Button type="submit" className="w-full sm:w-auto" disabled={busy}>
            {busy ? "Création…" : "Ajouter l'utilisateur"}
          </Button>
        </div>
      </form>
    </Card>
  );
}

/* ========================= helper ========================= */

function SubTabButton({ label, active, onClick }: { label: string; active?: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} className={cn("rounded-full px-4 py-2 text-sm border transition", active ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/80")}>
      {label}
    </button>
  );
}
